"use client"

import * as React from "react"
import { NumberField as NumberFieldPrimitive } from "@base-ui/react/number-field"
import { Minus, Plus } from "lucide-react"

import { cn } from "@/lib/utils"

// A number input modelled on the design-kit CNumberInput: a filled group with the value centered
// between an optional decrement/increment pair, and no native browser spinners. Steppers are
// opt-in via `controls` (table cells enable them). Commit is left to the consumer — `onValueChange`
// fires while editing, `onCommit` fires once focus leaves the whole group.
// Ghost steppers that stay out of the way: hidden until the row or the field is hovered/focused,
// and only pick up a background on their own hover. Keeps table cells quiet — just the number —
// while the controls remain one hover away. `group/row` is the board row; `group/numfield` is set
// on the group below so the reveal also works outside a table (e.g. the detail panel).
const stepButtonClass =
  "flex aspect-square shrink-0 select-none items-center justify-center self-stretch my-0.5 rounded-sm text-muted-foreground/60 opacity-0 transition-[color,background-color,opacity] hover:bg-muted hover:text-foreground focus-visible:opacity-100 group-hover/row:opacity-100 group-hover/numfield:opacity-100 group-focus-within/numfield:opacity-100 disabled:pointer-events-none disabled:!opacity-0 [&_svg]:size-3"

export function NumberInput({
  ariaLabel,
  className,
  controls = false,
  disabled,
  inputClassName,
  max,
  min,
  onCommit,
  onValueChange,
  placeholder = "—",
  prefix,
  step = 1,
  suffix,
  value,
}: {
  ariaLabel?: string
  className?: string
  controls?: boolean
  disabled?: boolean
  inputClassName?: string
  max?: number
  min?: number
  onCommit?: () => void
  onValueChange?: (value: number | null) => void
  placeholder?: string
  prefix?: string
  step?: number
  suffix?: string
  value: number | null
}) {
  return (
    <NumberFieldPrimitive.Root className="inline-flex w-fit min-w-0 max-w-full" disabled={disabled} max={max} min={min} onValueChange={(next) => onValueChange?.(next)} step={step} value={value}>
      <NumberFieldPrimitive.Group
        className={cn(
          "group/numfield inline-flex w-fit min-w-0 max-w-full items-center gap-1 rounded-md border border-transparent bg-transparent px-1 transition-colors hover:bg-muted/60 focus-within:border-input focus-within:bg-background focus-within:hover:bg-background",
          className,
        )}
        onBlur={(event) => {
          if (!event.currentTarget.contains(event.relatedTarget as Node | null)) onCommit?.()
        }}
        onPointerDown={(event) => event.stopPropagation()}
      >
        {controls ? (
          <NumberFieldPrimitive.Decrement className={stepButtonClass} disabled={disabled}>
            <Minus />
          </NumberFieldPrimitive.Decrement>
        ) : null}
        {prefix ? <span className="pointer-events-none pl-0.5 text-sm text-muted-foreground">{prefix}</span> : null}
        <NumberFieldPrimitive.Input
          aria-label={ariaLabel}
          className={cn(
            // Sizes to the number (min 2.5ch so a lone digit isn't cramped) and grows for longer
            // values instead of stretching to fill the whole cell.
            "h-full min-w-[2.5ch] max-w-full border-0 bg-transparent px-1 text-center text-sm font-medium tabular-nums outline-none [field-sizing:content] placeholder:font-normal placeholder:text-muted-foreground/60 [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none",
            inputClassName,
          )}
          onKeyDown={(event) => {
            if (event.key === "Enter") event.currentTarget.blur()
          }}
          placeholder={placeholder}
        />
        {suffix ? <span className="pointer-events-none pr-0.5 text-sm text-muted-foreground">{suffix}</span> : null}
        {controls ? (
          <NumberFieldPrimitive.Increment className={stepButtonClass} disabled={disabled}>
            <Plus />
          </NumberFieldPrimitive.Increment>
        ) : null}
      </NumberFieldPrimitive.Group>
    </NumberFieldPrimitive.Root>
  )
}
